import React, { useState } from 'react';
import {
  FiCheckCircle as CheckCircle,
  FiXCircle as XCircle,
  FiAlertCircle as AlertCircle,
  FiDollarSign as DollarSign,
  FiCalendar as Calendar,
  FiShield as Shield,
  FiX as X,
  FiBarChart2 as BarChart2,
  FiPieChart as PieChart
} from 'react-icons/fi';
import { format, differenceInDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { isPolicyCurrent } from '../utils/policyStatus';
import { currentPolicies } from '../utils/policyStatus';
import CoverageComparisonChart from './CoverageComparisonChart';
import { AGENCY_PHONE_TEL } from '../constants/contact';

const PolicyComparison = () => {
  const { user } = useAuth();
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [activeView, setActiveView] = useState('table'); // 'table' or 'charts'

  const handlePolicySelect = (policyId) => {
    if (selectedPolicies.includes(policyId)) {
      setSelectedPolicies(selectedPolicies.filter(id => id !== policyId));
    } else if (selectedPolicies.length < 3) {
      setSelectedPolicies([...selectedPolicies, policyId]);
    }
  };

  const getSelectedPolicyObjects = () => {
    return currentPolicies(user?.policies).filter(p => selectedPolicies.includes(p.id));
  };

  const compareFeature = (policies, feature) => {
    return policies.map(policy => {
      switch (feature) {
        case 'premium':
          return {
            value: policy.premium,
            display: `$${policy.premium.toFixed(2)}/${policy.paymentFrequency}`,
            score: policy.premium
          };
        case 'deductible':
          const deductible = policy.coverageDetails?.deductible || 500;
          return {
            value: deductible,
            display: `$${deductible}`,
            score: deductible
          };
        case 'coverage':
          const coverage = policy.coverageDetails?.coverageAmount || 50000;
          return {
            value: coverage,
            display: `$${coverage.toLocaleString()}`,
            score: coverage
          };
        case 'daysUntilRenewal':
          const days = differenceInDays(new Date(policy.expirationDate), new Date());
          return {
            value: days,
            display: `${days} days`,
            score: days
          };
        default:
          return { value: null, display: 'N/A', score: 0 };
      }
    });
  };

  if (showComparison && selectedPolicies.length > 0) {
    const policies = getSelectedPolicyObjects();

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Policy Comparison
              </h1>
              <p className="text-gray-600">
                Comparing {policies.length} {policies.length === 1 ? 'policy' : 'policies'}
              </p>
            </div>
            <button
              onClick={() => setShowComparison(false)}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <div className="flex gap-2 border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => setActiveView('table')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                  activeView === 'table'
                    ? 'bg-[#DC143C] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <BarChart2 size={18} />
                Table View
              </button>
              <button
                onClick={() => setActiveView('charts')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors ${
                  activeView === 'charts'
                    ? 'bg-[#DC143C] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <PieChart size={18} />
                Chart View
              </button>
            </div>
          </div>
        </div>

        {/* Chart View */}
        {activeView === 'charts' && (
          <CoverageComparisonChart 
            policies={policies}
            compareFeature={compareFeature}
          />
        )}

        {/* Table View */}
        {activeView === 'table' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 w-48">
                      Feature
                    </th>
                    {policies.map((policy) => (
                      <th
                        key={policy.id}
                        className="px-6 py-4 text-left text-sm font-semibold text-gray-900"
                      >
                        <div className="space-y-1">
                          <div className="text-base font-bold">{policy.policyType}</div>
                          <div className="text-xs text-gray-600 font-normal">
                            {policy.vendor}
                          </div>
                          <div className="text-xs text-gray-500 font-normal">
                            {policy.policyNumber}
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {/* Premium Comparison */}
                  <ComparisonRow
                    label="Premium"
                    icon={DollarSign}
                    policies={policies}
                    feature="premium"
                    compareFeature={compareFeature}
                  />

                  {/* Deductible Comparison */}
                  <ComparisonRow
                    label="Deductible"
                    icon={DollarSign}
                    policies={policies}
                    feature="deductible"
                    compareFeature={compareFeature}
                  />

                  {/* Coverage Amount */}
                  <ComparisonRow
                    label="Coverage Amount"
                    icon={Shield}
                    policies={policies}
                    feature="coverage"
                    compareFeature={compareFeature}
                  />

                  {/* Days Until Renewal */}
                  <ComparisonRow
                    label="Days Until Renewal"
                    icon={Calendar}
                    policies={policies}
                    feature="daysUntilRenewal"
                    compareFeature={compareFeature}
                  />

                  {/* Status */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <AlertCircle size={18} className="text-gray-500" />
                        <span className="font-medium text-gray-900">Status</span>
                      </div>
                    </td>
                    {policies.map((policy) => {
                      const current = isPolicyCurrent(policy);
                      return (
                        <td key={policy.id} className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                              current
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {current ? (
                              <>
                                <CheckCircle size={14} />
                                Active
                              </>
                            ) : (
                              <>
                                <AlertCircle size={14} />
                                Expired
                              </>
                            )}
                          </span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Coverage Features */}
                  <tr className="bg-gray-50">
                    <td colSpan={policies.length + 1} className="px-6 py-3">
                      <div className="font-semibold text-gray-900">Coverage Features</div>
                    </td>
                  </tr>

                  {/* Collision Coverage */}
                  <FeatureRow
                    label="Collision Coverage"
                    policies={policies}
                    featureKey="collision"
                  />

                  {/* Comprehensive Coverage */}
                  <FeatureRow
                    label="Comprehensive Coverage"
                    policies={policies}
                    featureKey="comprehensive"
                  />

                  {/* Roadside Assistance */}
                  <FeatureRow
                    label="Roadside Assistance"
                    policies={policies}
                    featureKey="roadside"
                  />

                  {/* Rental Car Coverage */}
                  <FeatureRow
                    label="Rental Car Coverage"
                    policies={policies}
                    featureKey="rental"
                  />
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detailed Policy Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {policies.map((policy) => (
            <div
              key={policy.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {policy.policyType}
                </h3>
                <p className="text-sm text-gray-600">{policy.vendor}</p>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Policy Number:</span>
                  <span className="font-medium text-gray-900">
                    {policy.policyNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Effective Date:</span>
                  <span className="font-medium text-gray-900">
                    {format(new Date(policy.effectiveDate), 'MMM dd, yyyy')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expiration Date:</span>
                  <span className="font-medium text-gray-900">
                    {format(new Date(policy.expirationDate), 'MMM dd, yyyy')}
                  </span>
                </div>
              </div>

              {policy.insuredItems && policy.insuredItems.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500 mb-2">Insured Items:</div>
                  {policy.insuredItems.map((item, index) => (
                    <div key={index} className="text-sm text-gray-700">
                      {item.type === 'vehicle'
                        ? `${item.year} ${item.make} ${item.model}`
                        : item.address}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Recommendations */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">
            Our Recommendations
          </h3>
          <div className="space-y-3 text-blue-800">
            <p>
              • Consider consolidating your policies with one carrier for potential
              multi-policy discounts
            </p>
            <p>
              • Review your coverage annually to ensure you're not over or under-insured
            </p>
            <p>
              • Ask about bundling discounts - combining auto and home insurance can save
              15-25%
            </p>
            <p>
              • Keep your policies current to avoid coverage gaps and potential rate
              increases
            </p>
          </div>
          <div className="mt-6">
            <a
              href={AGENCY_PHONE_TEL}
              className="inline-flex items-center gap-2 bg-[#DC143C] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#B01030] transition-colors"
            >
              Call for Personalized Advice
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Compare Your Policies
        </h1>
        <p className="text-gray-600">
          Select 2-3 policies to compare coverage, costs, and features side-by-side
        </p>
      </div>

      {/* Selection Counter */}
      {selectedPolicies.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-blue-600" />
              <span className="font-medium text-blue-900">
                {selectedPolicies.length} {selectedPolicies.length === 1 ? 'policy' : 'policies'} selected
              </span>
            </div>
            <div className="flex gap-3">
              {selectedPolicies.length >= 2 && (
                <button
                  onClick={() => setShowComparison(true)}
                  className="bg-[#DC143C] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#B01030] transition-colors"
                >
                  Compare Now
                </button>
              )}
              <button
                onClick={() => setSelectedPolicies([])}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Policy Selection Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentPolicies(user?.policies).map((policy) => {
          const isSelected = selectedPolicies.includes(policy.id);
          const canSelect = selectedPolicies.length < 3 || isSelected;

          return (
            <button
              key={policy.id}
              onClick={() => canSelect && handlePolicySelect(policy.id)}
              disabled={!canSelect}
              className={`text-left p-6 rounded-xl border-2 transition-all duration-200 ${
                isSelected
                  ? 'border-[#DC143C] bg-red-50 shadow-md'
                  : canSelect
                  ? 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                  : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {policy.policyType}
                  </h3>
                  <p className="text-sm text-gray-600">{policy.vendor}</p>
                </div>
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'border-[#DC143C] bg-[#DC143C]'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {isSelected && <CheckCircle size={16} className="text-white" />}
                </div>
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Premium:</span>
                  <span className="font-semibold text-gray-900">
                    ${policy.premium.toFixed(2)}/{policy.paymentFrequency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Policy #:</span>
                  <span className="font-medium text-gray-900">
                    {policy.policyNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expires:</span>
                  <span className="font-medium text-gray-900">
                    {format(new Date(policy.expirationDate), 'MMM dd, yyyy')}
                  </span>
                </div>
              </div>

              {isSelected && (
                <div className="pt-4 border-t border-red-200">
                  <span className="text-sm font-medium text-[#DC143C]">
                    Selected for comparison
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Instructions */}
      {selectedPolicies.length === 0 && (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <Shield size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Select Policies to Compare
          </h3>
          <p className="text-gray-600">
            Click on 2-3 policy cards above to begin your comparison
          </p>
        </div>
      )}

      {selectedPolicies.length === 1 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertCircle size={32} className="mx-auto mb-3 text-yellow-600" />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">
            Select One More Policy
          </h3>
          <p className="text-yellow-800">
            You need at least 2 policies to create a comparison
          </p>
        </div>
      )}
    </div>
  );
};

// Helper Component: Comparison Row
const ComparisonRow = ({
  label,
  icon: Icon,
  policies,
  feature,
  compareFeature
}) => {
  const comparisonData = compareFeature(policies, feature);

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-gray-500" />
          <span className="font-medium text-gray-900">{label}</span>
        </div>
      </td>
      {comparisonData.map((data, index) => (
        <td key={index} className="px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {data.display}
            </span>
          </div>
        </td>
      ))}
    </tr>
  );
};

// Helper Component: Feature Row
const FeatureRow = ({ label, policies, featureKey }) => {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <span className="text-sm font-medium text-gray-900">{label}</span>
      </td>
      {policies.map((policy) => {
        const hasFeature = policy.coverageDetails?.additionalCoverage?.includes(featureKey);
        return (
          <td key={policy.id} className="px-6 py-4">
            {hasFeature ? (
              <CheckCircle size={20} className="text-green-600" />
            ) : (
              <XCircle size={20} className="text-gray-300" />
            )}
          </td>
        );
      })}
    </tr>
  );
};

export default PolicyComparison;